import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  Box,
  Button,
  ButtonMode,
  CloseIcon,
  Dialog,
  LoadingSpinner,
  TextField
} from 'edenred-ui';

import { useGetBenefitGroupsQuery } from '@epi-api/benefitGroups';
import { IChangeGroupModal } from '@epi-models/pages/Beneficiaries';

import { ChangeGroupModalContent } from './ChangeGroupModalContent';

export const ChangeGroupModal = ({
  isOpen,
  handleClose,
  handleConfirm,
  changeInProgress,
  selectedGroupId,
  setSelectedGroupId
}: IChangeGroupModal) => {
  const { t } = useTranslation();
  const { sizeExtraExtraLarge, gray85, fontSemiBold, sizeMedium } = useTheme();
  const { data: benefitGroups, isLoading } = useGetBenefitGroupsQuery();

  const groupOptions = useMemo(() => {
    return benefitGroups?.map(group => ({
      id: group.groupId,
      label: group.groupName
    }));
  }, [benefitGroups]);

  const selectedGroup = useMemo(() => {
    if (!selectedGroupId) return null;
    return benefitGroups?.find(group => group.groupId === selectedGroupId);
  }, [benefitGroups, selectedGroupId]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} disablePortal>
      <Dialog.Title>
        <Box position="absolute" right={16} top={16}>
          <Button
            id="beneficiaries-modal-close-btn"
            mode={ButtonMode.Link}
            onClick={handleClose}
          >
            <CloseIcon />
          </Button>
        </Box>
        <Box
          fontSize={sizeExtraExtraLarge}
          fontWeight={fontSemiBold}
          color={gray85}
        >
          {t('containers.beneficiaries.table.change_group_modal.header')}
        </Box>
      </Dialog.Title>
      <Dialog.Content>
        <Box fontSize={sizeMedium} fontWeight={fontSemiBold} color={gray85}>
          {t('containers.beneficiaries.table.change_group_modal.cta')}
        </Box>
        <Box maxWidth={400} py={2.5}>
          <TextField
            id="benefit-group-select"
            label={t(
              'containers.beneficiaries.table.change_group_modal.placeholder'
            )}
            select
            onChange={e => setSelectedGroupId(Number(e.target.value))}
            fullWidth
          >
            {groupOptions?.map(option => (
              <TextField.Item key={option.id} value={option.id}>
                {option.label}
              </TextField.Item>
            ))}
          </TextField>
        </Box>
        {selectedGroup && (
          <Box pb={3}>
            <ChangeGroupModalContent selectedGroup={selectedGroup} />
          </Box>
        )}
      </Dialog.Content>
      <Dialog.Actions>
        <Box
          display="flex"
          width="100%"
          alignItems="center"
          justifyContent="flex-end"
        >
          <Box display="flex" gap={2}>
            <Button
              id="cancel-benefit-settings"
              onClick={handleClose}
              mode={ButtonMode.Link}
              minWidth={135}
            >
              {t('containers.beneficiaries.table.change_group_modal.cancel')}
            </Button>
            <Button
              id="save-benefit-settings"
              mode={ButtonMode.Primary}
              minWidth={135}
              type="submit"
              form="beneficiary-form"
              onClick={() => {
                if (selectedGroupId) handleConfirm(selectedGroupId);
              }}
              disabled={!selectedGroupId || changeInProgress}
              isProcessing={changeInProgress}
            >
              {t('containers.beneficiaries.table.change_group_modal.confirm')}
            </Button>
          </Box>
        </Box>
      </Dialog.Actions>
    </Dialog>
  );
};
